//#region types
import { FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { SalaryFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
//#endregion
import { useSalaryDeviationsService } from "serviceHooks";
import { DateHelper, QueryStateHelper } from "utils/helpers";

function useSalarySpecificationReportFilterDefinition() {
  const salaryDeviationsService = useSalaryDeviationsService();

  // Options come from the people who have specifications in any year, not from the
  // full employee register. The endpoint takes no year, so someone whose payslips are
  // all from other years can still be picked and gives an empty list. The set is small,
  // so it is refetched on each query and matched client-side instead of through a
  // search endpoint.
  const searchEmployees = (searchStr: string) =>
    salaryDeviationsService.getSalarySpecificationUsers().then((res) => {
      const term = searchStr?.toLowerCase() ?? "";
      return (res.data ?? [])
        .filter((user) => (user.personName ?? "").toLowerCase().includes(term))
        .map(
          (user): AutocompleteListItem => ({
            id: user.id,
            value: user.personName ?? "",
            icon: IconDefinitionConstants.WUser,
          })
        );
    });

  const filterFormFields: GenericFormSection<SalaryFormTypes.SalarySpecificationFilter>[] = [
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        personName: {
          label: TranslationKeys.Common.Employee,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            referenceFieldName: FormFieldConstants.Salary.SalarySpecificationFilter.PersonId,
            items: searchEmployees,
          } as AutoCompleteExtraProps<SalaryFormTypes.SalarySpecificationFilter>,
        },
        year: {
          label: TranslationKeys.Common.YearPascal,
          type: FormEnums.FormFieldType.AUTOCOMPLETE,
          extraProps: {
            items: [...Array(10).keys()].map((i) => ({
              value: (DateHelper.getYear(DateHelper.today()) - i).toString(),
            })),
          } as AutoCompleteExtraProps<SalaryFormTypes.SalarySpecificationFilter>,
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<SalaryFormTypes.SalarySpecificationFilter> = {
    year: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Salary.SalarySpecificationFilter.Year),
    personId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Salary.SalarySpecificationFilter.PersonId),
    personName: QueryStateHelper.stringQueryEncoder(FormFieldConstants.Salary.SalarySpecificationFilter.PersonName),
  };

  return { filterFormFields, filterQueryStateHandler };
}

export default useSalarySpecificationReportFilterDefinition;
